/**
 * IDB CRUD operations для `quotes` store.
 *
 * Used by Phase D (Quote / КП generation):
 *   - saveQuote() — после редактирования 17 row spec'а или PDF generation
 *   - getQuote() — на mount экрана КП
 *   - listQuotesByProject() — все КП проекта (history)
 *
 * syncStatus state machine — mirror projects (ADR-0019 §Q11.4).
 *
 * Indexes:
 *   - by-projectId (для list quotes принадлежащих проекту)
 *   - by-syncStatus (для sync queue scan)
 *
 * References: ADR-0019 §Q11.2, §Q11.4, §Q9 (Quote 17 rows)
 */

import { openDb, type QuoteRecord, type SyncStatus } from './database';

export async function saveQuote(record: QuoteRecord): Promise<void> {
  const db = await openDb();
  await db.put('quotes', record);
}

export async function getQuote(id: string): Promise<QuoteRecord | undefined> {
  const db = await openDb();
  return db.get('quotes', id);
}

export async function deleteQuote(id: string): Promise<void> {
  const db = await openDb();
  await db.delete('quotes', id);
}

export async function listQuotesByProject(projectId: string): Promise<QuoteRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('quotes', 'by-projectId', projectId);
}

export async function listQuotesBySyncStatus(syncStatus: SyncStatus): Promise<QuoteRecord[]> {
  const db = await openDb();
  return db.getAllFromIndex('quotes', 'by-syncStatus', syncStatus);
}

export async function listAllQuotes(): Promise<QuoteRecord[]> {
  const db = await openDb();
  return db.getAll('quotes');
}

/** Update only syncStatus field (atomic transition pending → synced). */
export async function updateQuoteSyncStatus(id: string, syncStatus: SyncStatus): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('quotes', 'readwrite');
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    throw new Error(`QUOTE_NOT_FOUND: id=${id}`);
  }
  await tx.store.put({ ...existing, syncStatus });
  await tx.done;
}

/** Bulk count для UI badge (e.g., OnlineIndicator pending count). */
export async function countQuotesBySyncStatus(syncStatus: SyncStatus): Promise<number> {
  const db = await openDb();
  return db.countFromIndex('quotes', 'by-syncStatus', syncStatus);
}
